/**
 * ActivityPage — the team's execution status & history.
 *
 * Inbox-styled (v1's only view; architecture supports Kanban/Timeline/Table
 * later — see docs/product/jobs-and-activity.md §5.2). Four sections:
 *
 *   - Needs Attention  (failed + awaiting_input)
 *   - Active           (running)
 *   - Recently completed
 *   - Older history
 *
 * Scope toggle (Mine | Team) + status/template/window filters parse to
 * `?filter=key:value` URL chips. URL state survives reloads and is the
 * crosswalk landing from Jobs-page "View runs" links.
 */

import { useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Avatar } from '@/components/Avatar/Avatar';
import { InfoHint, InfoHintLead, InfoHintSection } from '@/components/InfoHint/InfoHint';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { Icon } from '@/lib/icon';
import { t } from '@/lib/i18n';
import { useInfoHintPulse } from '@/features/onboarding';
import { useActivityInbox, useMarkActivitySeen } from '../hooks';
import type { ActivityRow } from '../schemas';
import type { ActivityFetchOpts } from '../api';
import type { JobStatus } from '@/features/jobs/schemas';
import styles from './ActivityPage.module.css';

const STATUS_VARIANT: Record<JobStatus, 'info' | 'success' | 'warning' | 'danger'> = {
  running: 'info',
  awaiting_input: 'warning',
  completed: 'success',
  failed: 'danger',
  cancelled: 'info',
};

const STATUS_LABEL: Record<JobStatus, string> = {
  running: t('In progress'),
  awaiting_input: t('Needs input'),
  completed: t('Done'),
  failed: t('Failed'),
  cancelled: t('Cancelled'),
};

const ACCENT_CLASS: Record<JobStatus, string> = {
  running: styles.accentRunning ?? '',
  awaiting_input: styles.accentAttention ?? '',
  completed: styles.accentDone ?? '',
  failed: styles.accentFailed ?? '',
  cancelled: styles.accentCancelled ?? '',
};

export function ActivityPage() {
  const [params, setParams] = useSearchParams();
  const filters = params.getAll('filter');
  const opts = useMemo<ActivityFetchOpts>(() => parseFilters(filters), [filters]);
  const { data, isLoading, isError } = useActivityInbox(opts);
  const markSeen = useMarkActivitySeen();
  const pulsing = useInfoHintPulse();

  const activityInfoHint = (
    <InfoHint
      label={t('Activity')}
      tooltip={t("What your team is doing right now and what's been delivered.")}
      pulsing={pulsing}
      modal={{
        title: t('Activity'),
        body: (
          <>
            <InfoHintLead>
              {t(
                "Activity is your team's live feed — what's running, what's finished, and what needs your attention. Everything your team delivers comes through here.",
              )}
            </InfoHintLead>
            <InfoHintSection icon="🔄" heading={t('Running jobs')}>
              {t(
                "Jobs in progress show live status updates as your team works. Click any row to open the job and see what's happening in real time.",
              )}
            </InfoHintSection>
            <InfoHintSection icon="✅" heading={t('Completed deliverables')}>
              {t(
                'When a job finishes, the result lands here. Open it to review the deliverable, download files, or run the same job again from the job detail page.',
              )}
            </InfoHintSection>
            <InfoHintSection icon="🔍" heading={t('Filters')}>
              {t(
                "Use Mine to focus on jobs you assigned, or Team to see everything across the roster. The time window filter helps when you're catching up after time away.",
              )}
            </InfoHintSection>
          </>
        ),
      }}
    />
  );

  // On mount, mark the user as having seen the page (clears unread dots
  // for rows currently older than now). The unread state on individual
  // rows updates on the next refetch.
  useEffect(() => {
    if (data) markSeen.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader
          title={t('Activity')}
          subtitle={t("What's running, what needs you, and what's done.")}
          titleAdornment={activityInfoHint}
        />
        <p className={styles.muted}>{t('Loading…')}</p>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className={styles.page}>
        <PageHeader
          title={t('Activity')}
          subtitle={t("What's running, what needs you, and what's done.")}
          titleAdornment={activityInfoHint}
        />
        <p className={styles.muted}>{t('Could not load Activity.')}</p>
      </div>
    );
  }

  const setScope = (scope: 'mine' | 'team') => {
    const next = filters.filter((f) => !f.startsWith('scope:'));
    next.push(`scope:${scope}`);
    setParams(new URLSearchParams(next.map((f) => ['filter', f])));
  };
  const setWindow = (win: '48h' | '7d' | '30d' | 'all') => {
    const next = filters.filter((f) => !f.startsWith('window:'));
    next.push(`window:${win}`);
    setParams(new URLSearchParams(next.map((f) => ['filter', f])));
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('Activity')}
        subtitle={t("What's running, what needs you, and what's done.")}
        titleAdornment={activityInfoHint}
      />

      <div className={styles.filterBar}>
        <div className={styles.scopeGroup}>
          <button
            type="button"
            className={data.scope === 'mine' ? styles.scopeActive : styles.scope}
            onClick={() => setScope('mine')}
          >
            {t('Mine')}
          </button>
          <button
            type="button"
            className={data.scope === 'team' ? styles.scopeActive : styles.scope}
            onClick={() => setScope('team')}
          >
            {t('Team')}
          </button>
        </div>
        <span className={styles.filterSep} aria-hidden>
          ·
        </span>
        <div className={styles.windowChips}>
          {(['48h', '7d', '30d', 'all'] as const).map((w) => (
            <button
              key={w}
              type="button"
              className={data.window === w ? styles.windowChipActive : styles.windowChip}
              onClick={() => setWindow(w)}
            >
              {w === 'all' ? t('All') : w}
            </button>
          ))}
        </div>
      </div>

      <Section
        title={t('Needs Attention')}
        alwaysShow
        rows={data.sections.needs_attention}
        scope={data.scope}
      />
      <Section title={t('Active')} rows={data.sections.active} scope={data.scope} />
      <Section
        title={t('Recently completed')}
        rows={data.sections.recently_completed}
        scope={data.scope}
      />
      <Section title={t('Older history')} rows={data.sections.older_history} scope={data.scope} />
    </div>
  );
}

function Section({
  title,
  rows,
  alwaysShow,
  scope,
}: {
  title: string;
  rows: ActivityRow[];
  alwaysShow?: boolean;
  scope: 'mine' | 'team';
}) {
  if (rows.length === 0 && !alwaysShow) return null;
  return (
    <section className={styles.section} aria-label={title}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          {title}
          {rows.length > 0 ? <span className={styles.sectionCount}>{rows.length}</span> : null}
        </h2>
      </div>
      {rows.length === 0 ? (
        <div className={styles.allClear}>{t('All clear — nothing needs attention right now.')}</div>
      ) : (
        <ul className={styles.list}>
          {rows.map((row) => (
            <Row key={row.id} row={row} scope={scope} />
          ))}
        </ul>
      )}
    </section>
  );
}

function Row({ row, scope }: { row: ActivityRow; scope: 'mine' | 'team' }) {
  const navigate = useNavigate();
  const memberName = [row.member_first_name, row.member_last_name].filter(Boolean).join(' ');
  const canReplay =
    row.status === 'completed' || row.status === 'failed' || row.status === 'cancelled';
  const canCancel = row.status === 'running';
  const canReply = row.status === 'awaiting_input';

  return (
    <li className={`${styles.row} ${ACCENT_CLASS[row.status]}`}>
      <div className={styles.rowMain}>
        <button
          type="button"
          className={styles.rowTitleBtn}
          onClick={() => {
            void navigate(`/jobs/${encodeURIComponent(row.id)}`);
          }}
        >
          <StatusPill variant={STATUS_VARIANT[row.status]} dot={row.status === 'running'}>
            {STATUS_LABEL[row.status]}
          </StatusPill>
          <span className={row.is_unread ? styles.rowTitleUnread : styles.rowTitleRead}>
            {row.title}
          </span>
        </button>
        {row.status === 'failed' && row.error_message ? (
          <p className={styles.rowError}>{row.error_message}</p>
        ) : null}
        <div className={styles.rowFooter}>
          {memberName ? (
            <>
              <Avatar name={memberName} size="sm" aria-hidden />
              <span className={styles.rowMember}>{row.member_first_name}</span>
            </>
          ) : (
            <span className={styles.rowMember}>—</span>
          )}
          {row.template_title && row.created_from_template_id ? (
            <>
              <span className={styles.rowSep}>·</span>
              <Link
                to={`/jobs#${encodeURIComponent(row.created_from_template_id)}`}
                className={styles.rowTemplate}
              >
                <Icon iconRef={row.template_icon} size={12} /> {row.template_title}
              </Link>
            </>
          ) : null}
          {scope === 'team' && row.submitter_first_name ? (
            <>
              <span className={styles.rowSep}>·</span>
              <span className={styles.rowSubmitter}>
                {t('by')} {row.submitter_first_name}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className={styles.rowMeta}>
        <span className={styles.rowTime}>{formatRelative(row.created_at)}</span>
        {canReply ? (
          <button
            type="button"
            className={styles.rowAction}
            onClick={() => {
              void navigate(`/jobs/${encodeURIComponent(row.id)}`);
            }}
          >
            {t('Reply')}
          </button>
        ) : null}
        {canCancel ? (
          <button type="button" className={styles.rowAction} disabled>
            {t('Cancel')}
          </button>
        ) : null}
        {canReplay ? (
          <button
            type="button"
            className={styles.rowAction}
            disabled={!row.created_from_template_id}
            onClick={() => {
              if (row.created_from_template_id) {
                void navigate(
                  `/jobs/new?template=${encodeURIComponent(row.created_from_template_id)}&runAgainOf=${encodeURIComponent(row.id)}`,
                );
              }
            }}
          >
            {t('Run again')}
          </button>
        ) : null}
      </div>
    </li>
  );
}

function parseFilters(raw: string[]): ActivityFetchOpts {
  const opts: ActivityFetchOpts = { statuses: [], memberIds: [], templateIds: [] };
  for (const entry of raw) {
    const colon = entry.indexOf(':');
    if (colon < 0) continue;
    const key = entry.slice(0, colon).trim().toLowerCase();
    const value = entry.slice(colon + 1).trim();
    if (!value) continue;
    if (key === 'status') opts.statuses!.push(value);
    else if (key === 'member') opts.memberIds!.push(value);
    else if (key === 'template') opts.templateIds!.push(value);
    else if (key === 'window' && ['48h', '7d', '30d', 'all'].includes(value)) {
      opts.window = value as '48h' | '7d' | '30d' | 'all';
    } else if (key === 'scope' && (value === 'mine' || value === 'team')) {
      opts.scope = value;
    }
  }
  return opts;
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return t('just now');
  if (m < 60) return `${m}m ${t('ago')}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${t('ago')}`;
  const d = Math.floor(h / 24);
  return `${d}d ${t('ago')}`;
}
